import { useInfiniteQuery } from "@tanstack/react-query";
import {
  NUMBER_RESULTS_TO_RENDER_FOR_PRO_RESEARCH_INITIAL_LOAD,
  NUMBER_RESULTS_TO_RENDER_FOR_SEARCH_INITIAL_LOAD,
} from "constants/config";
import {
  getMaxCitations,
  getPaperSearch,
  getProResearchResults,
  PaperSearchResponse,
  ProResearchResultsResponse,
  SearchMode,
} from "helpers/api";
import { getSearchTestingQueryParams } from "helpers/search";
import { useRouter } from "next/router";
import { useMemo, useRef } from "react";

// The interval at which we poll the Pro Research API to check
// if the search is complete.
const POLLING_INTERVAL = 4000;
const POLLING_LIMIT = (10 * 60 * 1000) / POLLING_INTERVAL; // 10 minutes
const PRO_RESEARCH_POLLING_RETRY_ON_FAILURE_LIMIT = 10;
const NON_PRO_RESEARCH_RETRY_ON_FAILURE_LIMIT = 2; // 2 attempts to retry the search.

export function getSearchQueryKey(searchId: string) {
  return ["search", searchId];
}

function useSearchById(
  searchId?: string,
  enabled: boolean = true,
  isProResearch: boolean = false,
  searchMode: SearchMode = SearchMode.PRO_ANALYSIS
) {
  // Run the search in "debug" mode if the debug query parameter is set to true
  const router = useRouter();
  const searchTestingParams = getSearchTestingQueryParams(router.query);
  const lang = router.query.lang as string;

  // Track the number of polling attempts
  const pollingCount = useRef(0);

  // If search mode is pro research, use the pro research initial load size
  const numResultsToLoad =
    searchMode === SearchMode.PRO_RESEARCH
      ? NUMBER_RESULTS_TO_RENDER_FOR_PRO_RESEARCH_INITIAL_LOAD
      : NUMBER_RESULTS_TO_RENDER_FOR_SEARCH_INITIAL_LOAD;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery(
    getSearchQueryKey(searchId!),
    async ({ pageParam = 0 }) => {
      if (isProResearch) {
        const proResearchParams = new URLSearchParams(searchTestingParams);
        if (lang) {
          proResearchParams.set("lang", lang);
        }
        return await getProResearchResults(
          searchId!,
          pageParam,
          proResearchParams,
          undefined,
          numResultsToLoad
        );
      } else {
        return await getPaperSearch(
          searchId!,
          pageParam,
          searchTestingParams,
          undefined,
          lang
        );
      }
    },
    {
      enabled: Boolean(searchId) && enabled,
      getNextPageParam: (lastPage, allPages) => {
        // Check if the last page is marked as the end
        if (!lastPage?.is_end) {
          return allPages.length; // Next page is the next index
        }
        return undefined;
      },
      staleTime: Infinity, // Keep search results in the local cache, because they won't change.
      refetchInterval: isProResearch
        ? (data) => {
            const isComplete =
              (data?.pages[0] as ProResearchResultsResponse)?.is_complete ??
              false;

            if (isComplete) {
              pollingCount.current = 0; // Reset counter when complete
              return false;
            }

            // Check if we've exceeded the polling limit
            if (pollingCount.current >= POLLING_LIMIT) {
              return false; // Stop polling after POLLING_LIMIT attempts
            }

            // Increment polling count
            pollingCount.current += 1;

            return POLLING_INTERVAL;
          }
        : undefined,
      retryDelay(failureCount, error) {
        // For 429 rate limiting errors, wait 1 minute before retry
        const isAxiosError =
          error && typeof error === "object" && "response" in error;
        const statusCode = isAxiosError
          ? (error as any).response?.status
          : null;

        if (statusCode === 429) {
          return 60000; // 1 minute
        }

        // For other errors, use exponential backoff
        return failureCount ** 2 * 500; // 1st attempt: 500ms, 2nd: 2000ms, 3rd: 4500ms, 4th: 8000ms, etc.
      },
      retry(failureCount, error) {
        if (process.env.NODE_ENV === "test") {
          return false;
        }

        const retryLimit = isProResearch
          ? PRO_RESEARCH_POLLING_RETRY_ON_FAILURE_LIMIT
          : NON_PRO_RESEARCH_RETRY_ON_FAILURE_LIMIT;

        return failureCount < retryLimit;
      },
      refetchIntervalInBackground: isProResearch,
      refetchOnWindowFocus: isProResearch, // If the user has abandoned a tab and the polling has timed out, fetch again on focus so that the deep search can still pick back up.
    }
  );

  // Rebuild the PaperSearchResponse object
  const results: (PaperSearchResponse & { is_complete?: boolean }) | undefined =
    useMemo(() => {
      const maxCitations = getMaxCitations(searchMode);

      return data
        ? {
            search_id: String(searchId),
            // Combine all papers into a single array
            papers: data.pages
              .flatMap((page) => page.papers)
              .map((paper, index) => ({
                ...paper,
                originalIndex: index < maxCitations ? index : undefined,
              })),
            is_end: data.pages[data.pages.length - 1]?.is_end ?? true,
            query_rewrite: data.pages[0]?.query_rewrite,
            isExtractedAnswers: data.pages[0]?.isExtractedAnswers,
            num_top_results: data.pages[0]?.num_top_results,
            num_relevant_results: data.pages[0]?.num_top_results,
            num_results_analyzed: data.pages[0]?.num_results_analyzed,
            query_features: data.pages[0]?.query_features,
            synthesis_features: data.pages[0]?.synthesis_features,
            is_complete: isProResearch
              ? ((data.pages[0] as ProResearchResultsResponse)?.is_complete ??
                false)
              : true,
          }
        : undefined;
    }, [data, searchId, isProResearch, searchMode]);

  return {
    results, // Combined papers
    pages: data?.pages, // Raw pages of results
    fetchNextPage, // Function to load the next page
    hasNextPage, // Indicates if there are more pages
    isError, // Error state
    isFetchingNextPage, // Loading state for pagination
    isLoading: isLoading && enabled && Boolean(searchId), // General loading state
    noResultsFound:
      !isLoading && results?.papers.length === 0 && results?.is_end,
    pageCount: data?.pages.length ?? 0,
    isComplete: results?.is_complete ?? (isProResearch ? false : true),
  };
}

export default useSearchById;
